import { ProgramPolicyItem, TabType } from '@/views/tower/v2/objects/ProgramsLibraryPanel';
import { Carrier, IssuingCompany } from '../Carrier';
import { PolicyType } from '../Programs';
import { Wholesaler, WholesalerGroup } from '../WholesalerGroup';
import { ProgramTower } from './v2/Tower';
import { TowerScaleMode } from '@/views/tower/v2/objects/tower_scale/TowerScale';

export type SelectionType = 'pan' | 'selection' | 'custom-policy-rect' | 'labeled-rect' | 'triangle' | 'text' | 'legend' | 'tower-scale';
export type BorderType = 'solid | dash | dotted';
export type TextStyle = 'serif' | 'sans-serif' | 'monospace';

export type CanvasChangeReason =
  | 'towerVersionCreated'
  | 'towerVersionDeleted'
  | 'towerVersionSelected'
  | 'programChanged'
  | 'undoTowerChange'
  | 'redoTowerChange'
  | 'towerHistoryReset'
  | 'canvasInitialLoad'
  | 'retentionContainerSelected';

export interface TowerCanvasState {
  currentTowerInstance?: Partial<ProgramTower>;
  selection: SelectionType;
  gridModeEnabled: boolean;
  legendEnabled: boolean;
  displayOptions: DisplayOptions;
  gridSquareSize: number;
  contextMenuState: TowerContextMenuState;
  selectedObject?: TowerObjectConfig;
  objectConfig?: TowerObjectConfig;
  zoomLevel: number;
  scrollX: number;
  scrollY: number;
  canvasObjects: fabric.Object[];
  publishPolicyModalState: PublishPolicyModalState;
  editDisplayOptionsState: EditDisplayOptionsState;
  editTowerInfoModalState: EditTowerInfoModalState;
  programsPanelState: ProgramsPanelState;
  programId?: number;
  accountId?: number;
  programName?: string;
  accountName?: string;
  programPolicies?: ProgramPolicyItem[];
  towerVersions: ProgramTower[];
  currentTowerVersion?: ProgramTower;
  duplicateFabricObject: boolean;
  deleteFabricObject: boolean;
  editMode: boolean; // this is for whether the tower builder is accessable
  loadError: boolean;
  canvasChangeReason?: CanvasChangeReason;
  towerHistoryStack: ProgramTower[];
  currentTowerHistoryIndex?: number;
  exportType: string;
  showExportOptions: boolean;
  activeCanvasObject?: fabric.Object;
  snapToModeEnabled: boolean;
  isEditing: boolean;
  isTowerEditorExpanded: boolean;
  isTowerEditorVisible: boolean;
  limitPremiumCounterEnabled: boolean;
  customPolicies: ProgramPolicyItem[];
  renewalProcessed?: boolean; // for renewal towers - set to true after policies are converted to custom policies on initial load
  isDeletedProgramMode: boolean;
}

export interface ProgramsPanelState {
  show: boolean;
  tab?: TabType;
  programId?: number;
}

export interface EditPolicyFormState {
  show: boolean;
  editPolicyId?: string;
}

export interface PublishPolicyModalState {
  show: boolean;
  policy?: ProgramPolicyItem;
  policyType?: PolicyType;
}

export interface EditDisplayOptionsState {
  show: boolean;
}

export interface EditTowerInfoModalState {
  show: boolean;
  towerId?: string;
}

export interface TowerContextMenuState {
  show: boolean;
  left: string;
  top: string;
  activeObjectId?: string;
  objectType?: string;
}

export interface TowerObjectConfig {
  ids: string[];
  types?: string[];
  opacity: number;
  fill: string;
  stroke: string;
  background: string;
  strokeWidth: number;
  strokeStyle: string;
  strokeDashArray: number[];
  fontFamily: string;
  textStyle: TextStyle;
  textColor: string;
  fontSize: number;
  borderType: BorderType;
  updatedProps?: string[];
  scaleMode?: TowerScaleMode;
  coverageTypeEnabled?: boolean;
  coverageContainerFill?: string;
  coverageContainerStroke?: string;
  width?: number;
  height?: number;
}

export interface Policy {
  carrierGroup?: Carrier; // aka insurer group
  issuingCompany?: IssuingCompany;
  wholesalerGroup?: WholesalerGroup;
  wholesaler?: Wholesaler;
  coverageType?: string;
  premium?: number;
  aggregateLimit?: number;
  occurrenceLimit?: number;
  retention?: number;
  attachmentPoint?: number;
  quotaShareTotal?: number;
}

export interface DisplayOptions {
  carrier: DisplayLabelOptions;
  amBest: DisplayLabelOptions;
  aggregateLimit: DisplayLabelOptions;
  occurrenceLimit: DisplayLabelOptions;
  aggregate: DisplayLabelOptions;
  occurrence: DisplayLabelOptions;
  premium: DisplayLabelOptions;
  coverage: DisplayLabelOptions;
  quotaSharePercent: DisplayLabelOptions;
}

export interface DisplayLabelOptions {
  enabled: boolean;
  customName: string;
}

export enum Label {
  CarrierLabel = 'carrier',
  AggregateLimitAndAttachPointLabel = 'aggregateLimit',
  OccurrenceLimitAndAttachPointLabel = 'occurrenceLimit',
  AggregateRateOnLineLabel = 'aggregate',
  OccurrenceRateOnLineLabel = 'occurrence',
  PremiumLabel = 'premium',
  AmBestLabel = 'amBest',
  CoverageLabel = 'coverage',
  QuotaSharePercent = 'quotaSharePercent',
  All = 'all',
}

export const DEFAULT_DISPLAY_LABELS = new Map<string, string>([
  [Label.CarrierLabel, 'Carrier'],
  [Label.AmBestLabel, 'AM Best'],
  [Label.AggregateLimitAndAttachPointLabel, 'Aggregate Limit'],
  [Label.OccurrenceLimitAndAttachPointLabel, 'Occurrence Limit'],
  [Label.AggregateRateOnLineLabel, 'Rate on Line (Aggregate)'],
  [Label.OccurrenceRateOnLineLabel, 'Rate on Line (Occurrence)'],
  [Label.PremiumLabel, 'Premium'],
]);

const fontFamilies = new Map<string, string>([
  ['serif', "Palatino, Cambria, 'Times New Roman', Times, serif"],
  ['sans-serif', "Calibri, Candara, Segoe, 'Segoe UI', Optima, Helvetica, Arial, sans-serif"],
  ['monospace', "'Lucida Console', Consolas, Monaco, Menlo, 'Courier New', Courier, monospace"],
]);

export const FONT_SIZES = [12, 18, 26, 36];

/** Based on the `TextStyle` type, return a font family */
export function fontFamily(style: string): string {
  return fontFamilies.get(style) || '';
}

const disabledConfigs = new Map<string, Set<string>>([
  ['labeled-rectangle', new Set<string>()],
  ['policy-rect', new Set<string>()],
  ['triangle', new Set<string>(['textStyle', 'fontFamily'])],
  ['i-text', new Set<string>(['stroke', 'strokeWidth', 'strokeStyle'])],
]);

/** Certain object types should selectively disallow properties to be set. For example, technically a border can be
 *  set on a text object, but practically it doesn't make sense in this app. This function, based on the
 *  `fabric.Object` `type` property, will determine if a property can be set.
 */
export function isConfigEnabledForObjectType(property: string, objectType?: string): boolean {
  if (!objectType) return false;
  const isDisabled = disabledConfigs.get(objectType)?.has(property) ?? false;
  return !isDisabled;
}

export function strokeDimensions(type: string, strokeWidth: number): number[] {
  switch (type) {
    case 'solid':
      return [0];
    case 'dashed':
      return [strokeWidth * 1.5, strokeWidth / 2];
    case 'dotted':
      return [strokeWidth, strokeWidth];
    default:
      return [0];
  }
}
